import _get from 'lodash/get';
import _isPlainObject from 'lodash/isPlainObject';
import assert from 'http-assert-plus';
import axios from 'axios';
import base64 from 'base-64';
import Vue from 'vue';
import { tryNull } from './utils';
import { ulid } from 'ulid';

const localStorage = 'localStorage' in global && global.localStorage ? global.localStorage : null;
const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

const CONFIDENTIAL_PAGEVIEW_PATHS = [
  '/login/confirm',
  '/onboarding/registernetwork',
  '/profile/sync-contacts',
];

const STATIC_PROPS = {
  referrer: tryNull(() => document.referrer) || undefined,
  // @link https://stackoverflow.com/questions/1091372/getting-the-clients-timezone-offset-in-javascript
  timezone: tryNull(() => Intl.DateTimeFormat().resolvedOptions().timeZone) || undefined,
  userAgent: tryNull(() => navigator.userAgent) || undefined,
};

let pageViewedAt = null;

export function captureLog(eventName, eventData) {
  assert(typeof eventName === 'string' && eventName.length, new TypeError('Expected eventName to be a string'));
  assert(_isPlainObject(eventData), new TypeError('Expected eventData to be a plain object'));

  const headers = {};

  const token = localStorage ? JSON.parse(localStorage.getItem('token')) : null;
  if (typeof token === 'string' && token.length) {
    headers.Authorization = (token.startsWith('admin ') || token.startsWith('user ')) ? token : `user ${token}`;
  }

  return axios.post('/api/logs', { eventName, ...eventData }, { headers }).catch(err => {
    Vue.$log.error(err, { eventName, ...eventData });
  });
}

export function captureError(eventName, err, eventData = undefined) {
  assert(typeof eventName === 'string' && eventName.length, new TypeError('Expected eventName to be a string'));
  assert(err instanceof Error || (typeof err === 'string' && err.length),
    new TypeError('Expected err to be an instance of Error or a string'));
  assert(eventData === undefined || _isPlainObject(eventData), new TypeError('Expected eventData to be a plain object'));

  const error = {
    name: err.name || 'Error',
    message: err.message || `${err}`,
    code: err.code || null,
  };

  return captureLog(eventName, { ...eventData, error });
}

export function capturePageView(route) {
  assert(route && route.path, new Error('Expected capturePageView argument to be the route'));
  const { pageView: additionalData } = route.meta || {};

  const lastPageView = sessionStorage ? JSON.parse(sessionStorage.getItem('pageview')) : null;
  const { session, page: lastViewedPage, count: lastViewedCount, time: lastViewedTime } = lastPageView || {};

  const now = new Date();

  const pageView = {
    session: session || ulid(),
    page: route.path,
    time: now.toISOString(),
    count: (lastViewedCount || 0) + 1,
    route: {
      name: route.name || undefined,
      path: route.path,
      params: _isPlainObject(route.params) && Object.keys(route.params).length ? route.params : undefined,
      query: _isPlainObject(route.query) && Object.keys(route.query).length ? route.query : undefined,
      hash: typeof route.hash === 'string' && route.hash.length ? route.hash : undefined,
    },
    lastViewedPage,
    lastViewedTime,
    ...STATIC_PROPS,
  };

  if (CONFIDENTIAL_PAGEVIEW_PATHS.includes(route.path)) {
    // Protect confidential routes from recording query strings & hash strings
    pageView.route.query = undefined;
    pageView.route.hash = undefined;
  }

  const eventData = {
    ...(typeof additionalData === 'function' ? additionalData.call(this, route.params || {}) : null),
    ...(_isPlainObject(additionalData) ? { ...additionalData } : null),
    pageView,
  };

  sessionStorage && sessionStorage.setItem('pageview', JSON.stringify(pageView));
  Vue.$log.debug('PAGE_VIEW', eventData);

  pageViewedAt = now.getTime();
  return captureLog('PAGE_VIEW', eventData);
}

export function preparePageClick(el, { value: data }, vnode) {
  const { session } = (sessionStorage ? JSON.parse(sessionStorage.getItem('pageview')) : null) || {};
  const { $route } = _get(vnode, 'fnContext.$root') || {};

  return {
    session,
    route: $route && $route.path ? { name: $route.name || undefined, path: $route.path } : undefined,
    ...data,
  };
}

export function capturePageClick(data) {
  const timeOnPage = pageViewedAt ? Date.now() - pageViewedAt : undefined;
  const eventData = { ...data, timeOnPage };
  Vue.$log.debug('PAGE_CLICK', eventData);
  return captureLog('PAGE_CLICK', { ...eventData, timeOnPage });
}

export function capturePageAction(data) {
  const { session } = (sessionStorage ? JSON.parse(sessionStorage.getItem('pageview')) : null) || {};
  const { $route } = this.$root || {};

  const timeOnPage = pageViewedAt ? Date.now() - pageViewedAt : undefined;
  const eventData = {
    session,
    route: $route && $route.path ? { name: $route.name || undefined, path: $route.path } : undefined,
    ...data,
    timeOnPage,
  };
  Vue.$log.debug('PAGE_ACTION', eventData);
  return captureLog('PAGE_ACTION', { ...eventData, timeOnPage });
}

function resolveTrackingRedirect(redirect) {
  if (typeof redirect === 'string' && (redirect.startsWith('https/') || redirect.startsWith('http/'))) {
    const [ protocol, hostname, ...path ] = redirect.split('/');
    const { search, hash } = process.env.VUE_ENV === 'server' ? {} : window.location;
    return `${protocol}://${hostname}/${path.join('/')}${search}${hash}`;
  } else {
    return `/${redirect || ''}`;
  }
}

export const trackingRoute = {
  name: 'Track',
  path: '/t/:eventData/:redirect*',
  component: {
    name: 'Track',
    render(el) {
      return el('div', { class: 'd-flex justify-content-center align-items-center my-5' }, [
        el('b-spinner', { props: { variant: 'primary' } }),
      ]);
    },
    mounted() {
      const { redirect } = this.$route.params || {};
      const resolved = resolveTrackingRedirect(redirect);
      this.$log.info({ redirect: resolved });

      if (typeof resolved === 'string' && resolved.startsWith('/')) {
        this.$router.replace(resolved);
      } else {
        window.location = resolved;
      }
    },
  },
  meta: {
    pageView({ eventData, redirect }) {
      const additional = {};

      try {
        additional.track = JSON.parse(base64.decode(eventData));
      } catch (err) {
        this.$log.error(err);
      }
      try {
        additional.redirect = resolveTrackingRedirect(redirect);
      } catch (err) {
        this.$log.error(err);
      }

      return additional;
    },
  },
};



// WEBPACK FOOTER //
// ./src/lib/logs.js